package com.swetdot.swetgram.data.discover

import com.swetdot.swetgram.data.api.ApiClient
import com.swetdot.swetgram.data.api.ApiError
import com.swetdot.swetgram.cache.SwetgramCache
import com.swetdot.swetgram.data.ioDispatcher
import com.swetdot.swetgram.data.toDomain
import com.swetdot.swetgram.domain.DiscoverComment
import com.swetdot.swetgram.domain.DiscoverCommentId
import com.swetdot.swetgram.domain.DiscoverCommentWrite
import com.swetdot.swetgram.domain.DiscoverContent
import com.swetdot.swetgram.domain.DiscoverFailure
import com.swetdot.swetgram.domain.DiscoverFeedCursors
import com.swetdot.swetgram.domain.DiscoverFeedId
import com.swetdot.swetgram.domain.DiscoverFeedPage
import com.swetdot.swetgram.domain.DiscoverFeedResult
import com.swetdot.swetgram.domain.DiscoverFeedValidator
import com.swetdot.swetgram.domain.DiscoverLikeResult
import com.swetdot.swetgram.domain.DiscoverPost
import com.swetdot.swetgram.domain.DiscoverPostId
import com.swetdot.swetgram.domain.DiscoverPostStatus
import com.swetdot.swetgram.domain.DiscoverPostType
import com.swetdot.swetgram.domain.DiscoverPlanDetails
import com.swetdot.swetgram.domain.DiscoverPlanDraft
import com.swetdot.swetgram.domain.DiscoverPostWrite
import com.swetdot.swetgram.domain.DiscoverAcceptedAnswer
import com.swetdot.swetgram.domain.DiscoverActivity
import com.swetdot.swetgram.domain.DiscoverActivityKind
import com.swetdot.swetgram.domain.DiscoverActivityPage
import com.swetdot.swetgram.domain.DiscoverLinkedPost
import com.swetdot.swetgram.domain.ChatId
import com.swetdot.swetgram.data.newUuid
import com.swetdot.swetgram.domain.GroupId
import com.swetdot.swetgram.domain.Page
import com.swetdot.swetgram.protocol.dto.CreateDiscoverCommentRequest
import com.swetdot.swetgram.protocol.dto.CreateDiscoverPostRequest
import com.swetdot.swetgram.protocol.dto.CreateDiscoverPlanRequest
import com.swetdot.swetgram.protocol.dto.EditDiscoverCommentRequest
import com.swetdot.swetgram.protocol.dto.EditDiscoverPostRequest
import com.swetdot.swetgram.protocol.dto.UpdateDiscoverPlanStatusRequest
import com.swetdot.swetgram.protocol.dto.AcceptDiscoverAnswerRequest
import com.swetdot.swetgram.protocol.dto.ClearDiscoverAnswerRequest
import com.swetdot.swetgram.protocol.dto.ShareDiscoverPostRequest
import com.swetdot.swetgram.protocol.dto.Message
import com.swetdot.swetgram.protocol.dto.ErrorCodes
import io.ktor.client.request.header
import io.ktor.client.request.parameter
import io.ktor.client.request.setBody
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpMethod
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlin.time.Clock
import kotlin.time.Instant
import com.swetdot.swetgram.protocol.dto.DiscoverComment as DiscoverCommentDto
import com.swetdot.swetgram.protocol.dto.DiscoverCommentPage as DiscoverCommentPageDto
import com.swetdot.swetgram.protocol.dto.DiscoverPost as DiscoverPostDto
import com.swetdot.swetgram.protocol.dto.DiscoverPostPage as DiscoverPostPageDto
import com.swetdot.swetgram.protocol.dto.DiscoverScope as DiscoverScopeDto
import com.swetdot.swetgram.protocol.dto.DiscoverContentStatus as DiscoverContentStatusDto
import com.swetdot.swetgram.protocol.dto.DiscoverContentType as DiscoverContentTypeDto
import com.swetdot.swetgram.protocol.dto.DiscoverActivityPage as DiscoverActivityPageDto
import com.swetdot.swetgram.protocol.dto.DiscoverActivityType as DiscoverActivityTypeDto
import com.swetdot.swetgram.protocol.ProtocolJson
import kotlinx.coroutines.withContext
import kotlinx.serialization.builtins.ListSerializer

/**
 * The Discover data layer: two feeds, posts, comments and likes.
 *
 * | operation                        | verb + path                                | here            |
 * |----------------------------------|--------------------------------------------|-----------------|
 * | `listGlobalDiscoverPosts`        | `GET    /discover/posts`                   | [feed], [poll]  |
 * | `createGlobalDiscoverPost`       | `POST   /discover/posts`                   | [createPost]    |
 * | `listGroupDiscoverPosts`         | `GET    /groups/{id}/discover/posts`       | [feed]          |
 * | `createGroupDiscoverPost`        | `POST   /groups/{id}/discover/posts`       | [createPost]    |
 * | `getDiscoverPost`                | `GET    /discover/posts/{post_id}`         | [post]          |
 * | `editDiscoverPost`               | `PATCH  /discover/posts/{post_id}`         | [editPost]      |
 * | `deleteDiscoverPost`             | `DELETE /discover/posts/{post_id}`         | [deletePost]    |
 * | `likeDiscoverPost`               | `POST   /discover/posts/{id}/like`         | [setPostLiked]  |
 * | `unlikeDiscoverPost`             | `DELETE /discover/posts/{id}/like`         | [setPostLiked]  |
 * | `listDiscoverComments`           | `GET    /discover/posts/{id}/comments`     | [comments]      |
 * | `createDiscoverComment`          | `POST   /discover/posts/{id}/comments`     | [addComment]    |
 * | `editDiscoverComment`            | `PATCH  /discover/comments/{id}`           | [editComment]   |
 * | `deleteDiscoverComment`          | `DELETE /discover/comments/{id}`           | [deleteComment] |
 * | `likeDiscoverComment`            | `POST   /discover/comments/{id}/like`      |[setCommentLiked]|
 * | `unlikeDiscoverComment`          | `DELETE /discover/comments/{id}/like`      |[setCommentLiked]|
 * | `shareDiscoverPostToConversation`| `POST   /discover/posts/{id}/share`        | ABSENT -- below |
 *
 * All fifteen of the first rows were exercised against the deployed server on 2026-08-06 before
 * this file was written; every claim below about what the server answers was probed, not inferred.
 *
 *
 * THERE IS NO SHARE CALL, AND THAT IS THE ONE PLACE THE FOLDER ASKS FOR SOMETHING NOTHING CAN SERVE.
 *
 * SPS §11.4 lists "share action" in the post card and SDS §13 repeats it under Actions. The frozen
 * contract does declare `shareDiscoverPostToConversation` -- so this is not a contract gap -- but the
 * deployed server does not route it: `POST /discover/posts/{id}/share` answers a **bodyless** 404,
 * with no `Content-Type` and no error envelope, and a server test pins that. A share method here
 * would be a method that cannot work, and a share button built on it would be a button that always
 * fails. Two consequences worth writing down:
 *
 *  * The 404 is NOT `ApiError.NotFound`. With no envelope to parse, [ApiClient] produces
 *    `ApiError.Server(404, null, "Unexpected response (404)")` -- so even a caller that wrote its own
 *    share call and mapped `NotFound` to "post is gone" would get a generic server fault instead. An
 *    unrouted path and a missing post are distinguishable, and they do not look alike.
 *  * `DiscoverPostReference` in `protocol/dto/Messages.kt` still does not match the contract (it
 *    invents `author` and `preview` and omits `author_display_name`, `content_preview` and `url`).
 *    Whoever builds the share must fix that first, or a shared post renders as a bare id.
 *
 *
 * THERE IS NO REPORT CALL EITHER, and that is scope rather than a gap: SPS §11.4's "report action" is
 * served by `reportContent` under the `Moderation` tag, not by a Discover operation.
 *
 *
 * NO GROUP MODERATION OF A GROUP'S OWN FEED. A group owner cannot remove a member's post from their
 * group's Discover area: no operation in v1.0.0 expresses it, SPS §12.7's permission table has no row
 * for it, and the server refuses. `DiscoverPost.mayDelete` is author-only for that reason.
 *
 *
 * The first page is cached for immediate offline-first rendering. It is never used as write
 * authority: every mutation still targets the server's resource version, and the screen refreshes
 * in the background after showing the cached page. [DiscoverFeedPage.fetchedAt] tells the UI when
 * that snapshot was obtained; sign-out and an access revocation both clear it.
 *
 *
 * NOTHING HERE IS IDEMPOTENT EXCEPT THE LIKES. Neither create body carries a `client_id` and neither
 * create operation takes an `Idempotency-Key`, so [ApiClient] is never given a key and therefore
 * never repeats a create -- a replayed `POST` makes a SECOND post. A timeout on a create is genuinely
 * ambiguous and must be surfaced as such rather than retried. The likes are the opposite: the contract
 * gives each like path a POST and a DELETE, each declaring the STATE it produces rather than flipping
 * one, so a repeat is safe by construction (probed: liking twice leaves exactly one like).
 *
 *
 * AUTHENTICATION. No Discover operation overrides the global `security: [bearerAuth]`, so every call
 * here is authenticated -- the default -- and none passes `authenticated = false`. Checked, because
 * getting that backwards on an operation the contract marks `security: []` deadlocks `SessionManager`.
 */
class DiscoverRepository(
    private val api: ApiClient,
    private val cache: SwetgramCache? = null,
    /** Injected so [DiscoverFeedPage.fetchedAt] is assertable without a real clock. */
    private val now: () -> Instant = { Clock.System.now() },
) {

    // ------------------------------------------------------------------------------- feeds

    /**
     * One page of a feed, walking BACKWARDS into history. Both feeds; the only way to a first page.
     *
     * Pass [DiscoverFeedCursors.Start] to load the top of the feed, then hand back
     * [DiscoverFeedPage.cursors] to walk further. The repository merges the cursors, so a caller
     * cannot lose its poll token by paging -- see [DiscoverFeedCursors], which is where the trap this
     * avoids is written out in full.
     *
     * NEWEST FIRST, always. SPS §11.2 and SDS §13 both fix the order as chronological newest-first,
     * the server pages an index declared to match, and the contract sets no order -- so a screen must
     * NOT re-sort a page or it will interleave pages wrongly and the cursor will stop meaning what
     * the server thinks it means.
     *
     * A GROUP FEED IS MEMBERS-ONLY, PUBLIC GROUPS INCLUDED (SPS §12.5). A caller who cannot see the
     * group gets [DiscoverFailure.UNAVAILABLE]; one who can see it but is not in it gets
     * [DiscoverFailure.NOT_A_MEMBER]. Neither refusal is declared by the contract; both are on the
     * record in `DECISIONS.md` §19.
     *
     * THE GLOBAL FEED CONTAINS ONLY GLOBAL POSTS -- probed by reading it with `limit=100` and finding
     * a single distinct scope. A group's posts never leak into it, so a screen does not have to
     * filter.
     */
    suspend fun feed(
        feed: DiscoverFeedId,
        cursors: DiscoverFeedCursors = DiscoverFeedCursors.Start,
        limit: Int = PAGE_SIZE,
        type: DiscoverPostType? = null,
    ): DiscoverFeedResult {
        val effective = limit.coerceIn(MIN_LIMIT, MAX_LIMIT)
        val path = feed.path()
        val read = readFeed(
            path,
            cursor = cursors.older,
            newerThan = null,
            limit = effective,
            cursors = cursors,
            type = type,
        )
        val at = now()
        val page = read.page ?: run {
            if (cursors.older == null) touchCachedHead(feed, type, at)
            return DiscoverFeedResult.Unchanged(cursors, at)
        }

        val loaded = DiscoverFeedPage(
                posts = page.items.map { it.toDomain() },
                cursors = DiscoverFeedCursors(
                    // The server emits `next_cursor` only WITH `has_more`; the `takeIf` is belt to
                    // that braces, so `hasMoreHistory` cannot be true with nothing to fetch.
                    older = page.pageInfo.nextCursor?.takeIf { page.pageInfo.hasMore },
                    // THE TRAP. `newer_cursor` arrives on the FIRST page of this direction and is
                    // NULL on every later one. Assigning it blindly would erase the poll token the
                    // moment the user scrolled, and every later poll would silently become a
                    // first-page read whose contents got prepended as though they were new.
                    newer = page.pageInfo.newerCursor ?: cursors.newer,
                    validator = read.validator,
                ),
                // Never true in this direction: the server hard-codes `has_newer: false` here, and
                // reading it as "nothing new exists" would be wrong -- it means "not asked".
                hasMoreNew = false,
                fetchedAt = at,
            )
        if (cursors.older == null) cacheHead(feed, type, page.items, loaded)
        return DiscoverFeedResult.Loaded(loaded)
    }

    /** Last successful first page, used only as an immediate offline-first rendering. */
    suspend fun cachedFeed(
        feed: DiscoverFeedId,
        type: DiscoverPostType? = null,
    ): DiscoverFeedPage? = withContext(ioDispatcher) {
        val row = cache?.cacheQueries?.selectCachedDiscoverFeed(feed.cacheKey(type))?.executeAsOneOrNull()
            ?: return@withContext null
        val posts = runCatching {
            ProtocolJson.decodeFromString(ListSerializer(DiscoverPostDto.serializer()), row.postsJson)
                .map { it.toDomain() }
        }.getOrElse {
            cache.cacheQueries.deleteCachedDiscoverFeed(feed.cacheKey(type))
            return@withContext null
        }
        DiscoverFeedPage(
            posts = posts,
            cursors = DiscoverFeedCursors(
                older = row.olderCursor,
                newer = row.newerCursor,
                validator = row.validatorRequestKey?.let { requestKey ->
                    row.validatorEtag?.let { etag -> DiscoverFeedValidator(requestKey, etag) }
                },
            ),
            hasMoreNew = false,
            fetchedAt = Instant.fromEpochMilliseconds(row.fetchedAt),
        )
    }

    suspend fun clearCachedFeed(feed: DiscoverFeedId, type: DiscoverPostType? = null) = withContext(ioDispatcher) {
        cache?.cacheQueries?.deleteCachedDiscoverFeed(feed.cacheKey(type))
    }

    private suspend fun cacheHead(
        feed: DiscoverFeedId,
        type: DiscoverPostType?,
        posts: List<DiscoverPostDto>,
        page: DiscoverFeedPage,
    ) = withContext(ioDispatcher) {
        cache?.cacheQueries?.upsertCachedDiscoverFeed(
            feedKey = feed.cacheKey(type),
            postsJson = ProtocolJson.encodeToString(ListSerializer(DiscoverPostDto.serializer()), posts),
            olderCursor = page.cursors.older,
            newerCursor = page.cursors.newer,
            validatorRequestKey = page.cursors.validator?.requestKey,
            validatorEtag = page.cursors.validator?.etag,
            fetchedAt = page.fetchedAt.toEpochMilliseconds(),
        )
    }

    /** A successful conditional head read refreshes the age of the stored snapshot too. */
    private suspend fun touchCachedHead(
        feed: DiscoverFeedId,
        type: DiscoverPostType?,
        checkedAt: Instant,
    ) = withContext(ioDispatcher) {
        cache?.cacheQueries?.touchCachedDiscoverFeed(
            fetchedAt = checkedAt.toEpochMilliseconds(),
            feedKey = feed.cacheKey(type),
        )
    }

    /**
     * Poll the GLOBAL feed for posts newer than the reader's top item. `newer_than`.
     *
     * GLOBAL ONLY, AND STRUCTURALLY SO -- this method takes no [DiscoverFeedId]. `listGroupDiscoverPosts`
     * declares `cursor`, `limit` and `If-None-Match` and NOT `newer_than`, and the deployed server does
     * not merely refuse the parameter there, it SILENTLY IGNORES IT: `GET /groups/{id}/discover/posts?
     * newer_than=xyz` answers `200` with the ordinary first page (probed with a deliberately malformed
     * token, which the global feed rejects with a 400). A client that reused one polling path for both
     * feeds would therefore receive a whole first page every time it polled a group, prepend it as new,
     * and duplicate the entire feed. Making the group case unrepresentable is the only fix that cannot
     * be undone by a later edit. A group feed is refreshed by re-reading its first page, which its
     * ETag is there to make cheap.
     *
     * THE POLL IS THE ONLY THING THAT MAKES DISCOVER MOVE. There is no realtime frame and no push
     * notification for Discover anywhere in this deployment -- a comment on your own post notifies
     * nobody -- so nothing arrives unasked.
     *
     * CONTIGUITY IS GUARANTEED AND MATTERS. When more than [limit] posts arrived, the server returns
     * the OLDEST of them -- the ones immediately above what the caller already holds -- and sets
     * [DiscoverFeedPage.hasMoreNew]. Probed: polling with `limit=1` after two new posts returns the
     * older one. So a caller prepends the batch, and if `hasMoreNew` polls again immediately; it must
     * not assume it has caught up, and it must not reorder.
     *
     * @throws IllegalArgumentException when [DiscoverFeedCursors.canPoll] is false. A feed can only be
     * polled once a first page has established where its top is; check `canPoll` before arming a timer.
     */
    suspend fun poll(
        cursors: DiscoverFeedCursors,
        limit: Int = PAGE_SIZE,
        type: DiscoverPostType? = null,
    ): DiscoverFeedResult {
        val token = cursors.newer
        require(token != null) { "Read a first page before polling: there is no top item yet" }
        val effective = limit.coerceIn(MIN_LIMIT, MAX_LIMIT)
        val path = DiscoverFeedId.Global.path()
        // `cursor` and `newer_than` together are a 400 on both fields, deliberately -- they ask for
        // opposite directions from two different points. Only one is ever sent from here.
        val read = readFeed(
            path,
            cursor = null,
            newerThan = token,
            limit = effective,
            cursors = cursors,
            type = type,
        )
        val at = now()
        val page = read.page ?: return DiscoverFeedResult.Unchanged(cursors, at)

        return DiscoverFeedResult.Loaded(
            DiscoverFeedPage(
                posts = page.items.map { it.toDomain() },
                cursors = DiscoverFeedCursors(
                    // A poll answers `next_cursor: null, has_more: false` ALWAYS -- it says nothing
                    // about history, because the caller reached this response by walking upward and
                    // already holds everything below its token. Taking the null would throw away the
                    // history cursor and make "load more" restart from the top of the feed.
                    older = cursors.older,
                    // THE SAME TRAP, AND THIS IS ITS COMMONEST FORM. A poll that finds nothing
                    // answers `items: []` with `newer_cursor: null` (probed). Assigning it would
                    // destroy the poll token on the very first quiet poll, after which every later
                    // poll would send no `newer_than` at all.
                    newer = page.pageInfo.newerCursor ?: token,
                    validator = read.validator,
                ),
                hasMoreNew = page.pageInfo.hasNewer,
                fetchedAt = at,
            ),
        )
    }

    // -------------------------------------------------------------------------------- posts

    /** `getDiscoverPost`. Absent, deleted, invisible and blocked are one 404, on purpose. */
    suspend fun post(id: DiscoverPostId): DiscoverPost =
        api.get<DiscoverPostDto>("discover/posts/${id.value}").toDomain()

    /** Server-synced bookmarks, ordered by the original post chronology. */
    suspend fun savedPosts(cursor: String? = null, limit: Int = PAGE_SIZE): Page<DiscoverPost> {
        val page: DiscoverPostPageDto = api.get("discover/saved") {
            parameter("cursor", cursor)
            parameter("limit", limit.coerceIn(MIN_LIMIT, MAX_LIMIT))
        }
        return Page(
            items = page.items.map { it.toDomain() },
            nextCursor = page.pageInfo.nextCursor,
            hasMore = page.pageInfo.hasMore,
        )
    }

    suspend fun activity(cursor: String? = null, limit: Int = PAGE_SIZE): DiscoverActivityPage {
        val page: DiscoverActivityPageDto = api.get("discover/activity") {
            parameter("cursor", cursor)
            parameter("limit", limit.coerceIn(MIN_LIMIT, MAX_LIMIT))
        }
        return DiscoverActivityPage(
            items = page.items.map { item ->
                DiscoverActivity(
                    id = item.id,
                    kind = when (item.type) {
                        DiscoverActivityTypeDto.COMMENT -> DiscoverActivityKind.COMMENT
                        DiscoverActivityTypeDto.MENTION_POST -> DiscoverActivityKind.MENTION_POST
                        DiscoverActivityTypeDto.MENTION_COMMENT -> DiscoverActivityKind.MENTION_COMMENT
                        DiscoverActivityTypeDto.ANSWER_ACCEPTED -> DiscoverActivityKind.ANSWER_ACCEPTED
                        DiscoverActivityTypeDto.UNKNOWN -> DiscoverActivityKind.UNKNOWN
                    },
                    actor = item.actor.toDomain(),
                    postId = DiscoverPostId(item.postId),
                    commentId = item.commentId?.let(::DiscoverCommentId),
                    preview = item.preview,
                    createdAt = parseInstantOrNull(item.createdAt),
                    readAt = parseInstantOrNull(item.readAt),
                )
            },
            nextCursor = page.pageInfo.nextCursor,
            hasMore = page.pageInfo.hasMore,
            unreadCount = page.unreadCount.coerceAtLeast(0),
        )
    }

    suspend fun markActivityRead() {
        api.post<Unit>("discover/activity/read")
    }

    /** Absolute save state setter; both operations are idempotent and return the fresh post. */
    suspend fun setPostSaved(id: DiscoverPostId, saved: Boolean): DiscoverPost {
        val path = "discover/posts/${id.value}/saved"
        val dto = if (saved) api.put<DiscoverPostDto>(path) else api.delete<DiscoverPostDto>(path)
        return dto.toDomain()
    }

    suspend fun explore(query: com.swetdot.swetgram.domain.DiscoverBrowseQuery, cursor: String? = null): Page<DiscoverPost> {
        val page: DiscoverPostPageDto = api.get("discover/explore") {
            parameter("q", query.text.trim().takeIf { it.isNotEmpty() })
            parameter("content_type", query.type?.wireName())
            parameter("period", query.period.name.lowercase())
            parameter("sort", if (query.popular) "popular" else "newest")
            parameter("cursor", cursor)
            parameter("limit", PAGE_SIZE)
        }
        return Page(page.items.map { it.toDomain() }, page.pageInfo.nextCursor, page.pageInfo.hasMore)
    }

    /** Shares an immutable Discover preview into an existing conversation. */
    suspend fun sharePost(id: DiscoverPostId, chatId: ChatId, note: String? = null) {
        val clientId = newUuid()
        api.post<Message>(
            path = "discover/posts/${id.value}/share",
            idempotencyKey = newUuid(),
        ) {
            setBody(
                ShareDiscoverPostRequest(
                    conversationId = chatId.value,
                    clientId = clientId,
                    note = note,
                ),
            )
        }
    }

    /**
     * `createGlobalDiscoverPost` or `createGroupDiscoverPost`, chosen by [feed]. 201.
     *
     * ONE METHOD FOR BOTH, because the contract `$ref`s ONE request schema for both operations and
     * takes the scope from the PATH, never from the body -- so there is nothing to keep in step.
     *
     * NEVER RETRIED. See the note at the head of this file: there is no `client_id` and no
     * `Idempotency-Key`, so a repeat creates a second post and [ApiClient] is deliberately given
     * nothing that would let it retry. A caller must not paper over a timeout with its own retry.
     *
     * Requires a verified email; refuses a plain member of an `admins_only` group with
     * [DiscoverFailure.POSTING_RESTRICTED]. DECISIONS §12 caps posting at 30 a day.
     *
     * Trimmed and length-checked here so an over-long post costs no round trip -- `maxLength: 1000`
     * is a guaranteed `400 validation_failed` (probed at 1001 characters).
     */
    suspend fun createPost(
        feed: DiscoverFeedId,
        content: String,
        type: DiscoverPostType = DiscoverPostType.IDEA,
        plan: DiscoverPlanDraft? = null,
        sourcePostId: DiscoverPostId? = null,
    ): DiscoverPost {
        val text = content.trim()
        require(text.isNotEmpty()) { "A Discover post needs some text" }
        require(text.length <= DiscoverContent.POST_MAX) {
            "A Discover post is at most ${DiscoverContent.POST_MAX} characters"
        }
        require((type == DiscoverPostType.PLAN) == (plan != null)) {
            "Plan details are required only for a plan post"
        }
        return api.post<DiscoverPostDto>(feed.path()) {
            setBody(
                CreateDiscoverPostRequest(
                    content = text,
                    contentType = type.toWire(),
                    plan = plan?.let {
                        CreateDiscoverPlanRequest(
                            startsAt = it.startsAt.toString(),
                            place = it.place.trim(),
                            capacity = it.capacity,
                        )
                    },
                    sourcePostId = sourcePostId?.value,
                ),
            )
        }.toDomain()
    }

    suspend fun joinPlan(id: DiscoverPostId): DiscoverPost =
        api.post<DiscoverPostDto>("discover/posts/${id.value}/participants").toDomain()

    suspend fun leavePlan(id: DiscoverPostId): DiscoverPost =
        api.delete<DiscoverPostDto>("discover/posts/${id.value}/participants").toDomain()

    suspend fun updatePlanStatus(
        id: DiscoverPostId,
        status: DiscoverPostStatus,
        version: Int,
    ): DiscoverPost {
        require(status == DiscoverPostStatus.COMPLETED || status == DiscoverPostStatus.CANCELLED)
        return api.post<DiscoverPostDto>("discover/posts/${id.value}/status") {
            setBody(UpdateDiscoverPlanStatusRequest(status.toWire(), version))
        }.toDomain()
    }

    suspend fun acceptAnswer(
        postId: DiscoverPostId,
        commentId: DiscoverCommentId,
        version: Int,
    ): DiscoverPost = api.put<DiscoverPostDto>("discover/posts/${postId.value}/accepted-answer") {
        setBody(AcceptDiscoverAnswerRequest(commentId.value, version))
    }.toDomain()

    suspend fun clearAcceptedAnswer(postId: DiscoverPostId, version: Int): DiscoverPost =
        api.delete<DiscoverPostDto>("discover/posts/${postId.value}/accepted-answer") {
            setBody(ClearDiscoverAnswerRequest(version))
        }.toDomain()

    /**
     * `editDiscoverPost`. Author only, within 48 hours of writing it.
     *
     * RETURNS A SEALED RESULT RATHER THAN THROWING ON THE CONFLICT, exactly as
     * `GroupRepository.updateSettings` does and for the same reason: a lost race is not something a
     * screen can report and walk away from, because the composer is now describing a revision that no
     * longer exists. The re-read happens here, once, and arrives as [DiscoverPostWrite.Stale]. If the
     * RE-READ itself fails, that error propagates as its own `ApiError` rather than being swallowed
     * into a conflict the client cannot substantiate.
     *
     * A VERSION HIGHER THAN THE SERVER'S IS ALSO STALE -- probed with `version: 99`, which answers
     * `409 version_conflict` -- because the client is echoing a revision this server never issued.
     *
     * A LAPSED WINDOW IS NOT A CONFLICT TO RECOVER FROM. `409 delete_window_expired` propagates and
     * maps to [DiscoverFailure.EDIT_WINDOW_EXPIRED]; no re-read makes the post editable again.
     * [DiscoverPost.mayEdit] should have hidden the control, but the window can lapse while the
     * composer is open, so the refusal still has to be handled.
     */
    suspend fun editPost(id: DiscoverPostId, content: String, version: Int): DiscoverPostWrite {
        val text = content.trim()
        require(text.isNotEmpty()) { "An edited post still needs some text" }
        require(text.length <= DiscoverContent.POST_MAX) {
            "A Discover post is at most ${DiscoverContent.POST_MAX} characters"
        }
        return try {
            val updated = api.patch<DiscoverPostDto>("discover/posts/${id.value}") {
                setBody(EditDiscoverPostRequest(content = text, version = version))
            }
            DiscoverPostWrite.Updated(updated.toDomain())
        } catch (conflict: ApiError.VersionConflict) {
            // Nothing was written. Hand back what the server holds now so the composer can be
            // rebuilt in one step, without the caller having to know a conflict implies a refetch.
            DiscoverPostWrite.Stale(post(id))
        }
    }

    /**
     * `deleteDiscoverPost`. 204. Author only, at ANY time -- DECISIONS §8 sets no window on deleting,
     * unlike editing.
     *
     * Takes the post's comments and likes with it. A group's owner cannot call this on somebody
     * else's post; see the head of this file.
     */
    suspend fun deletePost(id: DiscoverPostId) {
        api.delete<Unit>("discover/posts/${id.value}")
    }

    // ----------------------------------------------------------------------------- comments

    /**
     * `listDiscoverComments`, cursor-paginated. The post detail screen only (SPS §11.6, SDS §13).
     *
     * OLDEST FIRST -- the opposite of the feed, and the server's own order. A thread reads downward
     * like a conversation; a screen must not re-sort it.
     *
     * PLAIN [Page], NOT [DiscoverFeedPage]. A thread is genuinely unidirectional: this operation
     * declares `cursor` and `limit` and NO `If-None-Match`, so it never answers 304 and never carries
     * a poll token. Reusing the feed's richer type would advertise two capabilities that do not exist.
     * Confirmed against the server: the comment list sends no `ETag` header at all.
     */
    suspend fun comments(
        postId: DiscoverPostId,
        cursor: String? = null,
        limit: Int = PAGE_SIZE,
    ): Page<DiscoverComment> {
        val page: DiscoverCommentPageDto = api.get("discover/posts/${postId.value}/comments") {
            // Ktor drops a null parameter, so a first page carries no cursor at all.
            parameter("cursor", cursor)
            parameter("limit", limit.coerceIn(MIN_LIMIT, MAX_LIMIT))
        }
        return Page(
            items = page.items.map { it.toDomain() },
            nextCursor = page.pageInfo.nextCursor,
            hasMore = page.pageInfo.hasMore,
        )
    }

    /**
     * `createDiscoverComment`. 201. Not idempotent, for the reason [createPost] states.
     *
     * DECISIONS §12 caps commenting at 100 a day. Requires a verified email.
     */
    suspend fun addComment(postId: DiscoverPostId, content: String): DiscoverComment {
        val text = content.trim()
        require(text.isNotEmpty()) { "A comment needs some text" }
        require(text.length <= DiscoverContent.COMMENT_MAX) {
            "A comment is at most ${DiscoverContent.COMMENT_MAX} characters"
        }
        return api.post<DiscoverCommentDto>("discover/posts/${postId.value}/comments") {
            setBody(CreateDiscoverCommentRequest(text))
        }.toDomain()
    }

    /**
     * `editDiscoverComment`. Author only, within 48 hours.
     *
     * [DiscoverCommentWrite.Stale] CARRIES NOTHING, unlike [DiscoverPostWrite.Stale], and the
     * asymmetry is the contract's: there is no `getDiscoverComment` in v1.0.0. The only read of a
     * comment is this paged listing, and a comment's page cannot be located from its id -- so the
     * caller has to re-list the thread. Fetching the whole thread from inside a failed write would
     * hide an unbounded cost in an error path.
     */
    suspend fun editComment(id: DiscoverCommentId, content: String, version: Int): DiscoverCommentWrite {
        val text = content.trim()
        require(text.isNotEmpty()) { "An edited comment still needs some text" }
        require(text.length <= DiscoverContent.COMMENT_MAX) {
            "A comment is at most ${DiscoverContent.COMMENT_MAX} characters"
        }
        return try {
            val updated = api.patch<DiscoverCommentDto>("discover/comments/${id.value}") {
                setBody(EditDiscoverCommentRequest(content = text, version = version))
            }
            DiscoverCommentWrite.Updated(updated.toDomain())
        } catch (conflict: ApiError.VersionConflict) {
            DiscoverCommentWrite.Stale
        }
    }

    /**
     * `deleteDiscoverComment`. 204, and silent about everything it did not do.
     *
     * A comment that never existed, one already deleted and one belonging to somebody else all answer
     * the same 204, because the server addresses the row by `(id, author_id)`. That is what makes it
     * retry-safe and what stops it being a comment-id oracle -- and it means a 204 here is NOT
     * evidence that anything was removed. A screen should re-list rather than trust it.
     */
    suspend fun deleteComment(id: DiscoverCommentId) {
        api.delete<Unit>("discover/comments/${id.value}")
    }

    // -------------------------------------------------------------------------------- likes

    /**
     * Set whether this viewer likes [id]. `likeDiscoverPost` / `unlikeDiscoverPost`.
     *
     * ABSOLUTE, NOT A TOGGLE. The caller says what the heart should BE, never "flip it". The contract
     * gives the like path a POST and a DELETE -- four operations across the two like paths, each
     * declaring the state it produces -- so a retried tap on a dropped response cannot undo a like,
     * and this signature is what carries that guarantee up to the screen.
     *
     * THE TAP IS OPTIMISTIC AND THIS RECONCILES IT. A screen applies [DiscoverPost.withLike] the
     * instant the heart is pressed and then settles for whatever comes back:
     * [DiscoverLikeResult.Settled] replaces the card with what the server actually holds -- re-read,
     * because [DiscoverPost.likeCount] is not this viewer's to predict while other people are liking
     * the same post -- and [DiscoverLikeResult.Failed] means put the heart back.
     *
     * HOW A LIKE AND AN UNLIKE RACING ARE HANDLED, which is the whole reason this method is not three
     * lines. Two things happen, and they are different:
     *
     *  1. PER-ITEM SERIALISATION. Every write for one post goes through that post's own [Mutex], so a
     *     `POST /like` and a `DELETE /like` for the same post can never be in flight together and can
     *     never be reordered by the network. Without it the server's final state is decided by which
     *     request happened to arrive last, which is not the tap the user made last. The lock is
     *     per-post, so liking one post never waits on another.
     *  2. LAST INTENT WINS, AND EARLIER ONES ARE DROPPED ENTIRELY. Each call takes a ticket before
     *     queueing. When it reaches the front, it re-checks whether it is still the newest intent for
     *     that post; if a later tap has arrived it returns [DiscoverLikeResult.Superseded] and sends
     *     NOTHING. So a like-unlike-like burst costs one request, not three, and the state the server
     *     ends in is the state the user last asked for. Collapsing is only sound because these
     *     operations are absolute state setters: with a toggle endpoint, dropping a request would
     *     invert the result.
     *
     * [DiscoverLikeResult.Superseded] MEANS DO NOTHING -- in particular, do not revert. The heart on
     * screen belongs to the newer tap, which is still in flight and will settle it.
     *
     * WHY A FAILED WRITE IS NOT ALWAYS A REVERT. If the write succeeds and only the confirming re-read
     * fails, the like DID land, so [DiscoverLikeResult.Accepted] keeps the optimistic value rather
     * than putting the heart back on something the server is holding. The one exception is a 404 on
     * the re-read, which means the post went away between the two calls -- then the card goes too.
     *
     * DECISIONS §12 caps likes at 300 a day and post likes share that bucket with comment likes.
     * Unlikes are NOT metered -- the contract declares no 429 on either unlike -- so a revert is
     * always affordable.
     */
    suspend fun setPostLiked(id: DiscoverPostId, liked: Boolean): DiscoverLikeResult =
        serialize("post:${id.value}") {
            val path = "discover/posts/${id.value}/like"
            try {
                if (liked) api.post<Unit>(path) else api.delete<Unit>(path)
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: Throwable) {
                // Nothing was written. The caller puts the heart back.
                return@serialize DiscoverLikeResult.Failed(failure.asDiscoverFailure())
            }
            // Reconcile rather than assume. `getDiscoverPost` is the authoritative count.
            try {
                DiscoverLikeResult.Settled(post(id))
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (reread: Throwable) {
                val failure = reread.asDiscoverFailure()
                // Gone between the write and the read: the card should go with it. Anything else
                // leaves a write that DID land and a count this client cannot confirm.
                if (failure == DiscoverFailure.UNAVAILABLE) {
                    DiscoverLikeResult.Failed(failure)
                } else {
                    DiscoverLikeResult.Accepted
                }
            }
        }

    /**
     * Set whether this viewer likes a comment. `likeDiscoverComment` / `unlikeDiscoverComment`.
     *
     * The same serialisation and the same last-intent-wins rule as [setPostLiked] -- read that -- with
     * ONE guarantee weaker: success is [DiscoverLikeResult.Accepted], never
     * [DiscoverLikeResult.Settled], because there is no `getDiscoverComment` to reconcile against. The
     * optimistic count stands until the thread is next listed, which is the honest position; re-listing
     * a whole thread on every heart tap would cost far more than the count is worth.
     *
     * AN UNLIKE OF SOMETHING THAT IS GONE STILL ANSWERS 204. Probed on posts and the comment routes are
     * built the same way: `DELETE .../like` succeeds whether or not there was a like, and whether or not
     * the target still exists. So a successful unlike is not evidence the comment is still there.
     */
    suspend fun setCommentLiked(id: DiscoverCommentId, liked: Boolean): DiscoverLikeResult =
        serialize("comment:${id.value}") {
            val path = "discover/comments/${id.value}/like"
            try {
                if (liked) api.post<Unit>(path) else api.delete<Unit>(path)
                DiscoverLikeResult.Accepted
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: Throwable) {
                DiscoverLikeResult.Failed(failure.asDiscoverFailure())
            }
        }

    // ------------------------------------------------------------------- like serialisation

    /** Guards [locks] and [intents]. Never held across a network call. */
    private val gate = Mutex()

    /** One lock per liked item, so two items never wait on each other. */
    private val locks = mutableMapOf<String, Mutex>()

    /** The newest ticket issued for each item. An older ticket reaching the front has been superseded. */
    private val intents = mutableMapOf<String, Long>()

    private var tickets = 0L

    /**
     * Runs [write] as the newest intent for [key], or reports [DiscoverLikeResult.Superseded].
     *
     * See [setPostLiked] for what this buys and why it is sound. The bookkeeping is dropped once no
     * ticket is outstanding for an item, which is what keeps the two maps from growing for the life
     * of the session; an item with no entry in [intents] has no writer in flight or queued, so its
     * lock is safe to discard.
     */
    private suspend fun serialize(key: String, write: suspend () -> DiscoverLikeResult): DiscoverLikeResult {
        val ticket = gate.withLock {
            tickets += 1
            intents[key] = tickets
            tickets
        }
        // Taken and released before queueing: `gate` is never held while waiting for an item lock,
        // so the two mutexes cannot deadlock against each other.
        val lock = gate.withLock { locks.getOrPut(key) { Mutex() } }

        return lock.withLock {
            // BEFORE: superseded while queued, so this tap never reaches the network at all. That is
            // what collapses a like-unlike-like burst into one request.
            if (newestTicket(key) != ticket) return@withLock DiscoverLikeResult.Superseded
            try {
                val result = write()
                // AFTER: a later tap arrived while this one was on the wire. Its write is next in
                // this same queue, so it owns the outcome -- reporting ours would flash a value that
                // is already obsolete, and reverting on ours would undo the newer tap's heart.
                if (newestTicket(key) != ticket) DiscoverLikeResult.Superseded else result
            } finally {
                gate.withLock {
                    // Only the newest ticket clears the entry, so "no intent" always means "no
                    // writer in flight or queued" -- which is what makes discarding the lock safe.
                    if (intents[key] == ticket) {
                        intents.remove(key)
                        locks.remove(key)
                    }
                }
            }
        }
    }

    private suspend fun newestTicket(key: String): Long? = gate.withLock { intents[key] }

    // ------------------------------------------------------------------- conditional reading

    /**
     * A feed page, or null for a `304 Not Modified` -- plus the ETag that came with the response.
     *
     * THE 304 ARRIVES AS AN ERROR AND IS TURNED BACK INTO A VALUE HERE. [ApiClient] treats everything
     * outside 2xx as a failure, and a 304 has no body to parse, so it surfaces as
     * `ApiError.Server(304, null, "Unexpected response (304)")`. Left alone that would report an
     * unchanged feed as a server fault -- the trap `GroupRepository` documented for `listGroups` and
     * declined to walk into. It is safe to catch precisely: `ApiError.Server.isRetryable` is
     * `status >= 500`, so a 304 is thrown on the first attempt and never retried into a delay.
     */
    private class FeedRead(
        val page: DiscoverPostPageDto?,
        etag: String?,
        requestKey: String,
    ) {
        /** The validator to carry forward, already bound to the request that produced it. */
        val validator: DiscoverFeedValidator? =
            etag?.let { DiscoverFeedValidator(requestKey = requestKey, etag = it) }
    }

    /**
     * Reads one feed page, sending `If-None-Match` only when the caller holds a validator for this
     * exact request. The key includes scope, cursors, limit and type, so a page-two validator can
     * never make page one appear unchanged.
     */
    private suspend fun readFeed(
        path: String,
        cursor: String?,
        newerThan: String?,
        limit: Int,
        cursors: DiscoverFeedCursors,
        type: DiscoverPostType?,
    ): FeedRead {
        val key = requestKey(path, cursor, newerThan, limit, type)
        val validator = cursors.validator?.takeIf { it.requestKey == key }
        return try {
            val response = api.executeRawResponse(
                method = HttpMethod.Get,
                path = path,
                idempotencyKey = null,
            ) {
                parameter("cursor", cursor)
                parameter("newer_than", newerThan)
                parameter("limit", limit)
                parameter("content_type", type?.wireName())
                validator?.let { header(HttpHeaders.IfNoneMatch, it.etag) }
            }
            val page = ProtocolJson.decodeFromString<DiscoverPostPageDto>(response.body)
            // A 200 means the body changed. Its ETag replaces the caller's stale validator.
            FeedRead(page, etag = response.headers[HttpHeaders.ETag], requestKey = key)
        } catch (notModified: ApiError.Server) {
            if (notModified.status != NOT_MODIFIED) throw notModified
            // A 304 says the validator still describes the response, so it is carried forward as is.
            FeedRead(page = null, etag = validator?.etag, requestKey = key)
        }
    }

    /**
     * Identifies a feed request for validator matching. Opaque, never sent anywhere.
     *
     * The limit is part of it because the server's ETag depends on it: the same cursor read with a
     * different limit is a different page and must not share a validator.
     */
    private fun requestKey(
        path: String,
        cursor: String?,
        newerThan: String?,
        limit: Int,
        type: DiscoverPostType?,
    ): String = "$path|c=${cursor.orEmpty()}|n=${newerThan.orEmpty()}|l=$limit|t=${type?.wireName().orEmpty()}"

    private fun DiscoverFeedId.path(): String = when (this) {
        DiscoverFeedId.Global -> "discover/posts"
        is DiscoverFeedId.OfGroup -> "groups/${groupId.value}/discover/posts"
    }

    private fun DiscoverFeedId.cacheKey(type: DiscoverPostType?): String = when (this) {
        DiscoverFeedId.Global -> "global"
        is DiscoverFeedId.OfGroup -> "group:${groupId.value}"
    } + ":type:${type?.wireName() ?: "all"}"

    private companion object {
        /** DECISIONS §5's standard first page, matching the contract's `Limit` default. */
        const val PAGE_SIZE = 30

        /** The contract's `Limit`: `minimum: 1`, `maximum: 100`. */
        const val MIN_LIMIT = 1
        const val MAX_LIMIT = 100

        const val NOT_MODIFIED = 304
    }
}

// -------------------------------------------------------------------------------- failures

/**
 * The Discover meaning of a thrown [ApiError], so a ViewModel can branch without importing
 * `protocol` (IMPLEMENTATION_NOTES §10).
 *
 * CODES FIRST, STATUS SECOND, the order [ApiError.fromCode] already uses, because three different
 * Discover refusals share the 403 and mean three unrelated things to the user: "you are not the
 * author", "this group only lets admins post" and "you are not in this group".
 *
 * `delete_window_expired` IS A 409 WITHOUT BEING A VERSION CONFLICT, which is why [ApiError.Conflict]
 * has to be read by code rather than by status. The server uses one status for "somebody edited first"
 * and "you are too late", and those need different sentences: one is fixed by reloading, the other is
 * never fixed at all.
 *
 * `message_edit_not_allowed` IS THE CODE FOR A DISCOVER POST, oddly but genuinely -- the server reuses
 * the message vocabulary for "only the author can edit this". Mapped here rather than left to a screen
 * to recognise.
 *
 * Anything with no Discover meaning stays [DiscoverFailure.OTHER] and keeps whatever handling it
 * already has: offline, a timeout, a 5xx, an expired session, an upgrade demand. Flattening those in
 * would lose the difference between "the server refused you" and "there is no network".
 */
fun Throwable.asDiscoverFailure(): DiscoverFailure = when (this) {
    // Its own ApiError class, not a Conflict -- checked first so the code lookup below cannot shadow it.
    is ApiError.VersionConflict -> DiscoverFailure.STALE
    is ApiError.Conflict -> when (code) {
        ErrorCodes.DELETE_WINDOW_EXPIRED -> DiscoverFailure.EDIT_WINDOW_EXPIRED
        ErrorCodes.VERSION_CONFLICT -> DiscoverFailure.STALE
        ErrorCodes.PLAN_FULL -> DiscoverFailure.PLAN_FULL
        ErrorCodes.PLAN_CLOSED -> DiscoverFailure.PLAN_CLOSED
        else -> DiscoverFailure.OTHER
    }
    is ApiError.Forbidden -> when (code) {
        ErrorCodes.MESSAGE_EDIT_NOT_ALLOWED -> DiscoverFailure.NOT_THE_AUTHOR
        ErrorCodes.GROUP_POSTING_RESTRICTED -> DiscoverFailure.POSTING_RESTRICTED
        ErrorCodes.NOT_CONVERSATION_MEMBER -> DiscoverFailure.NOT_A_MEMBER
        ErrorCodes.EMAIL_NOT_VERIFIED -> DiscoverFailure.EMAIL_NOT_VERIFIED
        else -> DiscoverFailure.OTHER
    }
    // `conversation_not_found` covers "no such post" and "you cannot see it" alike, on purpose: a
    // separate code would turn any post id into a live existence check on a private group's feed.
    is ApiError.NotFound -> DiscoverFailure.UNAVAILABLE
    is ApiError.RateLimited -> DiscoverFailure.RATE_LIMITED
    is ApiError.Validation -> DiscoverFailure.INVALID
    else -> DiscoverFailure.OTHER
}

// --------------------------------------------------------------------------------- mapping

/*
 * The wire-to-domain boundary for this package.
 *
 * Forgiving about dates and strict about nothing else: pass 1 gave every required property of
 * `DiscoverPost` and `DiscoverComment` NO Kotlin default, so a response missing a count, a flag, a
 * version or a scope fails the decode rather than arriving as a plausible lie. That failure surfaces
 * as a `SerializationException`, which `asDiscoverFailure` reports as `OTHER` -- "the response was not
 * understood" -- rather than as a refusal by the server.
 */

/** An unreadable date degrades one field rather than failing a screen, as everywhere else. */
private fun parseInstantOrNull(raw: String?): Instant? =
    raw?.let { runCatching { Instant.parse(it) }.getOrNull() }

/**
 * `updated_at` becomes "when this was edited", or null when it never was.
 *
 * STRICTLY GREATER, so the equal timestamps an untouched row carries mean "not edited". Comparing the
 * parsed instants rather than the raw strings matters: two different spellings of the same moment are
 * equal instants and unequal strings, and a string comparison would put an "(edited)" marker on every
 * post the moment the server changed how it formats a timestamp.
 *
 * Null when `created_at` cannot be parsed: without it there is nothing to compare against, and
 * claiming every post was edited is worse than claiming none was.
 */
private fun editedAtOf(createdAtRaw: String, updatedAtRaw: String): Instant? {
    val updated = parseInstantOrNull(updatedAtRaw) ?: return null
    val created = parseInstantOrNull(createdAtRaw) ?: return null
    return updated.takeIf { it > created }
}

/**
 * `scope` + `group_id` collapse into one nullable group.
 *
 * `unknown` READS THE GROUP ID, which is not a guess: the server's
 * `discover_posts_group_iff_scope` CHECK is `((scope = 'group') = (group_id IS NOT NULL))`, so the two
 * columns cannot disagree and reading either one answers the question. The server's own projection
 * takes the same fallback in the same direction.
 *
 * A `group` scope with no id is not a state this schema can produce. If one ever arrived the post
 * would render without its group label rather than being hidden -- there is no leak in that direction,
 * because the server had already decided this viewer may read the post before sending it.
 */
private fun groupOf(scope: DiscoverScopeDto, groupId: String?): GroupId? = when (scope) {
    DiscoverScopeDto.GLOBAL -> null
    DiscoverScopeDto.GROUP, DiscoverScopeDto.UNKNOWN -> groupId?.let { GroupId(it) }
}

private fun DiscoverPostType.toWire(): DiscoverContentTypeDto = when (this) {
    DiscoverPostType.IDEA -> DiscoverContentTypeDto.IDEA
    DiscoverPostType.QUESTION -> DiscoverContentTypeDto.QUESTION
    DiscoverPostType.PLAN -> DiscoverContentTypeDto.PLAN
    DiscoverPostType.OUTCOME -> DiscoverContentTypeDto.OUTCOME
}

private fun DiscoverPostType.wireName(): String = when (this) {
    DiscoverPostType.IDEA -> "idea"
    DiscoverPostType.QUESTION -> "question"
    DiscoverPostType.PLAN -> "plan"
    DiscoverPostType.OUTCOME -> "outcome"
}

private fun DiscoverContentTypeDto.toDomain(): DiscoverPostType = when (this) {
    DiscoverContentTypeDto.QUESTION -> DiscoverPostType.QUESTION
    DiscoverContentTypeDto.PLAN -> DiscoverPostType.PLAN
    DiscoverContentTypeDto.OUTCOME -> DiscoverPostType.OUTCOME
    DiscoverContentTypeDto.IDEA, DiscoverContentTypeDto.UNKNOWN -> DiscoverPostType.IDEA
}

private fun DiscoverContentStatusDto.toDomain(type: DiscoverPostType): DiscoverPostStatus = when (this) {
    DiscoverContentStatusDto.SCHEDULED -> DiscoverPostStatus.SCHEDULED
    DiscoverContentStatusDto.SOLVED -> DiscoverPostStatus.SOLVED
    DiscoverContentStatusDto.COMPLETED -> DiscoverPostStatus.COMPLETED
    DiscoverContentStatusDto.CANCELLED -> DiscoverPostStatus.CANCELLED
    DiscoverContentStatusDto.OPEN -> DiscoverPostStatus.OPEN
    DiscoverContentStatusDto.UNKNOWN -> when (type) {
        DiscoverPostType.PLAN -> DiscoverPostStatus.SCHEDULED
        DiscoverPostType.OUTCOME -> DiscoverPostStatus.COMPLETED
        else -> DiscoverPostStatus.OPEN
    }
}

private fun DiscoverPostStatus.toWire(): DiscoverContentStatusDto = when (this) {
    DiscoverPostStatus.COMPLETED -> DiscoverContentStatusDto.COMPLETED
    DiscoverPostStatus.CANCELLED -> DiscoverContentStatusDto.CANCELLED
    DiscoverPostStatus.SCHEDULED -> DiscoverContentStatusDto.SCHEDULED
    DiscoverPostStatus.SOLVED -> DiscoverContentStatusDto.SOLVED
    DiscoverPostStatus.OPEN -> DiscoverContentStatusDto.OPEN
}

internal fun DiscoverPostDto.toDomain(): DiscoverPost {
    val domainType = contentType.toDomain()
    return DiscoverPost(
    id = DiscoverPostId(id),
    // The shared UserPublic mapping, so an author renders here exactly as in a chat header.
    author = author.toDomain(),
    group = groupOf(scope, groupId),
    content = content,
    // `minimum: 0` on the wire. Floored anyway: a negative count is unrenderable, and clamping is
    // cheaper than a decode failure on a field nobody's safety depends on.
    likeCount = likeCount.coerceAtLeast(0),
    likedByMe = likedByMe,
    savedByMe = savedByMe,
    commentCount = commentCount.coerceAtLeast(0),
    createdAt = parseInstantOrNull(createdAt),
    editedAt = editedAtOf(createdAt, updatedAt),
    version = version,
    type = domainType,
    status = status.toDomain(domainType),
    plan = plan?.let {
        DiscoverPlanDetails(
            startsAt = parseInstantOrNull(it.startsAt),
            place = it.place,
            capacity = it.capacity,
            participantCount = it.participantCount.coerceAtLeast(0),
            joinedByMe = it.joinedByMe,
        )
    },
    acceptedAnswer = acceptedAnswer?.let {
        DiscoverAcceptedAnswer(
            commentId = DiscoverCommentId(it.commentId),
            authorId = com.swetdot.swetgram.domain.UserId(it.authorId),
            contentPreview = it.contentPreview,
        )
    },
    source = source?.let {
        DiscoverLinkedPost(
            id = DiscoverPostId(it.id),
            type = it.contentType.toDomain(),
            contentPreview = it.contentPreview,
        )
    },
    derivedPosts = derivedPosts.map {
        DiscoverLinkedPost(
            id = DiscoverPostId(it.id),
            type = it.contentType.toDomain(),
            contentPreview = it.contentPreview,
        )
    },
    derivedPostCount = derivedPostCount.coerceAtLeast(0),
    )
}

internal fun DiscoverCommentDto.toDomain(): DiscoverComment = DiscoverComment(
    id = DiscoverCommentId(id),
    postId = DiscoverPostId(postId),
    author = author.toDomain(),
    content = content,
    likeCount = likeCount.coerceAtLeast(0),
    likedByMe = likedByMe,
    createdAt = parseInstantOrNull(createdAt),
    editedAt = editedAtOf(createdAt, updatedAt),
    version = version,
    isAccepted = isAccepted,
)
